import React from "react";
import {
    NeuroMessage,
    useProduct,
    useGuideConfig,
    useNeuroState,
} from "@ignitesales/neuro-application";
import { useAnswers } from "@ignitesales/neuro-application/src/neuro-guide/hooks";
import classnames from "classnames";
import { useThemeSettings } from "../hooks/useThemeSettings.mjs";
import Collapsible from "react-collapsible";
import BasicProduct from "./BasicProduct";
import { useDialogueGuideSettings } from "../hooks/useDialogueGuideSettings.mjs";
import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useActionTrigger } from "../hooks/useActionTrigger.mjs";
import { placeholderRegex } from "../utils/stringHelper.mjs";
import { conditionFulfilled } from "../utils/productHelper.mjs";
import GuideApiService from '@ignitesales/neuro-application/src/neuro-guide/services/GuideApiService.mjs';
import useProductButtonsMessages from "../hooks/useProductButtonsMessages.mjs";

const productComponentClass = "product";

const shouldTriggerProductAction = (trigger, productId, buttonId) => {
    if (trigger.type !== "productAction") return false;

    const matchesProduct =
        trigger.targetProductId === productId || !!trigger?.forAllProducts;

    const matchesButton =
        trigger.targetProductButtonId === buttonId ||
        !!trigger?.forAllProductButtons;

    return matchesProduct && matchesButton;
};

const allConditionsFulfilled = (conditions, answers, productCart) => {
    return conditions.reduce((result, condition) => {
        return result && conditionFulfilled(condition, answers, productCart);
    }, true);
};

const replaceUrlPlaceholders = (rawUrl, productContext) => {
    if (typeof rawUrl !== "string") return rawUrl;

    return rawUrl.replace(placeholderRegex, (placeHolder) =>
        GuideApiService.transformValueWithPlaceholders(
            placeHolder,
            productContext,
        ),
    );
};

const firstProductClientId = (product) => {
    const candidates = [
        product?.clientProductId,
        product?.clientProductIds,
        product?.clientId,
    ];

    for (const candidate of candidates) {
        const value = Array.isArray(candidate) ? candidate[0] : candidate;
        if (value !== undefined && value !== null && value !== "") return value;
    }

    return undefined;
};

const buildProductActionContext = (product) => {
    const productClientId = firstProductClientId(product);

    return {
        productId: product?.id,
        productClientId,
        product: {
            ...product,
            id: product?.id,
            clientProductId: productClientId,
        },
    };
};

const DialogueProductWrapper = ({
    className,
    product,
    ProductComponent,
    onExternalProductClickHook,
    ...otherProductProps
}) => {
    const { user, productCart } = useNeuroState();
    const answers = useAnswers();
    const neuroProductProps = useProduct(product);
    const {
        description,
        descriptionAbbreviated,
        disclosures,
        features,
        name,
        onProductActionClick,
        productId,
        url,
        onSelectClick,
        isSelected,
    } = neuroProductProps;
    const { customConfigs } = useGuideConfig();
    const { disableProductActions, disclosureDisplay } =
        useDialogueGuideSettings();
    const { getSummaryThemeConfigs } = useThemeSettings();
    const productMessages = useProductButtonsMessages(product.id);
    const { shouldCollapseProductInfo } = getSummaryThemeConfigs();
    const shouldShowProductActions = !disableProductActions;
    const componentClass = productComponentClass;
    const classes = classnames(componentClass, className);
    const productContext = {
        ...user,
        ...buildProductActionContext(product),
    };

    // Use action trigger hook to handle pop/slide actions
    const { triggerAction } = useActionTrigger({
        contextData: {
            user,
            customConfigs: customConfigs?.guideActionServiceMapper,
        },
    });

    const onProductAction = async (actionId, actionAppkey = null) => {
        if (typeof onExternalProductClickHook === "function") {
            await onExternalProductClickHook(actionAppkey, product);
        }

        onProductActionClick({
            actionName: "productApplyClicked",
            value: "completed",
        });

        console.log("product button clicked", product, actionId);
        const actions =
            await ActionService.getActionsByTriggerType("productAction");
        for (let action of actions) {
            for (let trigger of action.triggers) {
                if (
                    shouldTriggerProductAction(trigger, product.id, actionId) &&
                    allConditionsFulfilled(
                        trigger.conditions,
                        answers,
                        productCart,
                    )
                ) {
                    ActionService.triggerAction(
                        action,
                        trigger?._id,
                        await ActionService.extractActionParams(
                            action,
                            productContext,
                            user,
                            customConfigs?.guideActionServiceMapper,
                        ),
                    ).then((e) => {
                        console.log("action " + action.name + " was triggered");
                    });
                }
            }
        }
    };

    const renderProductFeatures = () => {
        return features.length ? (
            <div className={`${componentClass}__features`}>
                <NeuroMessage id="product.features.title" tagName="div" />
                <ol>
                    {features.map((f, i) => (
                        <li key={i}>
                            <NeuroMessage
                                id={`${productId}.features.${i + 1}`}
                                shouldHideIfNotFound={true}
                            />
                        </li>
                    ))}
                </ol>
            </div>
        ) : null;
    };

    const renderProductDisclosures = () => {
        const DisclosureList =
            disclosureDisplay === "bullet"
                ? "ul"
                : disclosureDisplay === "number"
                  ? "ol"
                  : "ul";
        return disclosures.length ? (
            <div className={`${componentClass}__disclosures`}>
                <NeuroMessage id="product.disclosures.title" tagName="div" />
                <DisclosureList>
                    {disclosures.map((f, i) => (
                        <li key={i}>
                            <NeuroMessage
                                id={`${productId}.disclosures.${i + 1}`}
                                shouldHideIfNotFound={true}
                            />
                        </li>
                    ))}
                </DisclosureList>
            </div>
        ) : null;
    };

    const _collapsibleProductInfoTitle = (isOpen) => (
        <div className="productCollapseTitle">
            <NeuroMessage
                id={`product.collapsible.${isOpen ? "open" : "closed"}.title`}
                tagName="span"
            />
            <i
                className={isOpen ? "fa fa-minus-circle" : "fa fa-plus-circle"}
            />
        </div>
    );

    const productFeaturesAndDisclosuresRender =
        shouldCollapseProductInfo && (features.length || disclosures.length) ? (
            <Collapsible
                contentInnerClassName="productCollapseSection"
                trigger={_collapsibleProductInfoTitle(false)}
                triggerWhenOpen={_collapsibleProductInfoTitle(true)}
                transitionTime={250}
            >
                {renderProductFeatures()}
                {renderProductDisclosures()}
            </Collapsible>
        ) : (
            <div>
                {renderProductFeatures()}
                {renderProductDisclosures()}
            </div>
        );

    const productActions = shouldShowProductActions && (
        <div className={`${componentClass}__actions`}>
            {productMessages.map((action) => {
                const actionUrl = replaceUrlPlaceholders(
                    action.url,
                    productContext,
                );
                const hasActionUrl =
                    typeof actionUrl === "string" && actionUrl.trim() !== "";

                return hasActionUrl ? (
                    <a
                        key={action.id}
                        href={actionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onProductAction(action.id)}
                        className="btn btn-secondary product-action-button"
                    >
                        {action.text || "Apply"}
                    </a>
                ) : (
                    <button
                        key={action.id}
                        type="button"
                        onClick={() => onProductAction(action.id)}
                        className="btn btn-secondary product-action-button"
                    >
                        {action.text || "Apply"}
                    </button>
                );
            })}
        </div>
    );

    const cartButton = (addIcon, removeIcon, messages = {}) => (
        <div className={`${componentClass}__cartButton`}>
            <button
                onClick={onSelectClick}
                className={`cart-action-button ${
                    isSelected
                        ? "cart-action-button--remove"
                        : "cart-action-button--add"
                }`}
            >
                <NeuroMessage
                    id={
                        isSelected
                            ? (messages.remove || "cart.remove")
                            : (messages.add || "cart.add")
                    }
                />
                {!isSelected && addIcon}
                {isSelected && removeIcon}
            </button>
        </div>
    );

    const dialogueProductProps = {
        renderProductDisclosuresFn: renderProductDisclosures,
        renderProductFeaturesFn: renderProductFeatures,
        productFeaturesAndDisclosuresRender:
            productFeaturesAndDisclosuresRender,
        productActionsRender: productActions,
        description,
        descriptionAbbreviated,
        name,
        productClassNames: classes,
        productUrl: url,
        cartButtonRenderFn: cartButton,
        onProductButtonClick: onProductAction,
        ...otherProductProps,
    };

    const Product = ProductComponent ? ProductComponent : BasicProduct;

    return (
        <>
            <Product
                product={product}
                neuroProductProps={neuroProductProps}
                {...dialogueProductProps}
            />
        </>
    );
};

export default DialogueProductWrapper;
export { productComponentClass, DialogueProductWrapper };
